import Contact from '@/classes/v2/Contact';
import Message from '@/classes/v2/Message';
import store from '@/store';
import { MESSAGE_STATE } from '@/constants/message';
import Attachment from '@/classes/v2/Attachment';
import { parseGroupMessageList, parseNumberField } from '@/lib/SMSNumberParser';

const DEFAULT_CONTACT = { number: 'N/A' };

/**
 * Conversation Class.
 *
 * @typedef {Object} Conversation
 * @typedef {Object} Message
 * @typedef {Object} Contact
 *
 * @module Conversation
 **/
export default class Conversation {

  /**
   * @constructor
   * @this {Conversation}
   *
   * @param {Object} Conversation information - RawConversation header or RawMessage
   **/
  constructor({
    from, fromContact, to, toContact, body, deliveredTime,
    originationTime, messageType, messageState, messageStatus,
    groupMessage, groupMessageKey, providerMessageId, attachments, rowId, hasAttachments
  }) {
    // It is possible fromContact may be received as null, so we need to ensure a default value here.
    // This may cause visual issues on the application, but will make it at least load.
    const cleanedFromContact = fromContact || DEFAULT_CONTACT;
    const cleanedToContact = toContact || DEFAULT_CONTACT;

    const fromNumber = from ? from : cleanedFromContact.number;
    const toNumber = to ? to : cleanedToContact.number;
    const currentUser = store.getters['user/currentUser'];
    const fromCurrentUser = currentUser.number === from;

    // NOTE: The contact id doesn't ever seem to be present in the contact object
    this.contactId = fromCurrentUser ? toContact?.id : fromContact?.id;
    this.contactNumber = fromCurrentUser ? toNumber : fromNumber;
    if(groupMessage && !groupMessageKey){
      //for new group chats first message, need to create groupMessageKey
      groupMessageKey = toNumber + fromNumber;
      groupMessageKey = groupMessageKey.split("+").sort().join("+");
    }
    if (groupMessage && groupMessageKey) {
      // If its a group message and there is a group message key present, for now we will just use the group message key as the contact number.
      groupMessageKey = groupMessageKey.replace(currentUser.number, "");
      this.contactNumber = groupMessageKey;
    }

    this.groupMessage = groupMessage;
    this.groupMesssageKey = groupMessageKey;
    this.messages = [];

    if (fromNumber && toNumber && body !== undefined) {
      this.messages.push(
        new Message({
          from,
          to,
          body,
          originationTime,
          deliveredTime,
          messageType,
          messageState,
          messageStatus,
          groupMessage,
          providerMessageId,
          attachments,
          hasAttachments,
          rowId
        })
      );
    }

    this.headerOriginationTime = originationTime;
    this.unreadMessages = 0;
  }

  /**
   * Public getters
   */

  /**
   * Returns a complete Contact instance of the remote party.
   *
   * @returns {Contact}
   */
  get contact() {
    const contact = store.getters['contactsV2/getContactById'](this.contactId) ||
      store.getters['contactsV2/getContactByNumber'](this.contactNumber);

    return contact ? contact : new Contact({ number: this.contactNumber });
  }

  /**
   * Returns a list of contacts derived from the source contact number.
   *
   * @returns {Contact}
   */
  get contacts() {
    const numberList = parseNumberField(this.contactNumber);
    const contactList = [];

    for (const number of numberList) {
      const contact = store.getters['contactsV2/getContactByNumber'](number);
      if (contact) {
        contactList.push(contact);
      }
      else {
        contactList.push(new Contact({ number: number }));
      }
    }

    return contactList;
  }

  /**
   * Get the conversation key based on the group message key or contact number.
   * @return The conversation key.
   */
  get conversationKey() {
    return this.groupMesssageKey ? this.groupMesssageKey : this.contact.number;
  }

  /**
   * Determine if this is a group conversation.
   * @return The flag on if it's a group conversation or not.
   */
  get isGroupConversation() {
    if (this.groupMessage) return true;

    const activeMessages = this.messages || [];
    for (const message of activeMessages) {
      if (message.groupMessage) {
        return true;
      }
    }

    return false;
  }

  /**
   * Returns the contact number as a unique identifier for a conversation.
   *
   * @returns {String}
   */
  get conversationId() {
    return this.conversationKey;
  }

  /**
   * Returns the Message with the oldest origination date.
   *
   * @returns {Message}
   */
  get oldestMessage() {
    return this.sortedMessages.length ? this.sortedMessages[0] : {};
  }

  /**
   * Returns the Message with the newest origination date.
   *
   * @returns {Message}
   */
  get newestMessage() {
    return this.sortedMessages.length ? this.sortedMessages[this.sortedMessages.length - 1] : {};
  }

  /**
   * Returns the list of messages sorted from oldest to newest message.
   *
   * @returns {Array<Message>}
   */
  get sortedMessages() {
    return this.messages.sort(Message.compare);
  }

  /**
   * Determines whether the messages list is empty or not, returning true or false as appropriate.
   *
   * @returns {Boolean}
   */
  get isEmpty() {
    return this.messages.length === 0;
  }

  /**
   * Determines whether the Conversation was created only using a header message,
   * returning true or false as appropriate.
   *
   * @returns {Boolean}
   */
  get isOnlyHeader() {
    return this.messages.length === 1 && this.messages[0].originationTime === this.headerOriginationTime;
  }

  /**
   * Public Methods
   */

  /**
   * Determines whether a given message origin is current user or not,
   * returning true or false as appropriate.
   *
   * @param {Message} Destructured Message
   *
   * @returns {Boolean}
   */
  isMyMessage({ from, to, groupMessage, messageDirection }) {
    const currentUser = store.getters['user/currentUser'];
    const contactNumber = this.contact.number || '';
    const isIncomingMessage = contactNumber === from && currentUser.number === to;
    const isOutgoingMessage = contactNumber === to && currentUser.number === from;
    let isIncomingGroupMessage = false;

    if (groupMessage && "INBOUND" === messageDirection) {
      // The contact number is in format +16102300810+16102300811
      // Create the inbound group key by adding the from number and the to number with your self removed
      const toWithoutSelf = to.replace(currentUser.number, '');
      const inboundGroupKey = `${from}${toWithoutSelf}`;
      const groupMessageKey = parseGroupMessageList({ to: inboundGroupKey }).sort().join('');
      const parsedContactNumber = parseNumberField(contactNumber).sort().join('');
      if (groupMessageKey === parsedContactNumber) {
        isIncomingGroupMessage = true;
      }
    }

    return isIncomingMessage || isOutgoingMessage || isIncomingGroupMessage;
  }

  /**
   * Inserts or updates a message by doing some verifications.
   *
   * @param {Object} rawMessage - Source message to add.
   * @param {Boolean} allowReadState - Allows to insert READ updates.
   */
  upsertMessage(rawMessage, allowReadState = false) {
    if (rawMessage.messageState === MESSAGE_STATE.read && !allowReadState) return;

    const message = new Message(rawMessage);
    let i = this.messages.length;
    if (i === 0) return this.messages.push(message);

    // Search backwards for performance improvement.
    while (--i >= 0) {
      if (this.messages[i].isMyUpdate(rawMessage, allowReadState)) {
        // 1. Update attachments if the update include some attachments.
        if (message.attachments && this.messages[i].attachments) {
          let prevAttachments = this.messages[i].attachments;
          this.messages[i].attachments = message.attachments.map(attachment => new Attachment(attachment));
          this.messages[i].attachments.forEach((newAttachment, idx) => {
            if (!newAttachment.content) {
              newAttachment.content = prevAttachments[idx].content;
            }
          });
        }

        // 2. Update origination time if no previous states.
        if (this.messages[i].stateHistory.length === 0) {
          this.messages[i].originationTime = message.originationTime;
        }

        // 3. Update current state history.
        this.messages[i].stateHistory.push(rawMessage.messageState);

        // 4. Update current status history.
        this.messages[i].statusHistory.push(rawMessage.messageStatus);

        // 5. Update readTime time.
        if (!this.messages[i].readTime) this.messages[i].readTime = message.readTime;

        // 6. Update delivered time.
        if (!this.messages[i].deliveredTime) this.messages[i].deliveredTime = message.deliveredTime;

        // 7. Message updated, end loop and function.
        return;
      }
    }
    this.messages.push(message);

    const currentUser = store.getters['user/currentUser'];
    const activeConversationId = store.getters['conversationsV2/activeConversationId'];
    if (currentUser.number !== message.from &&
        (this.conversationId !== activeConversationId || !document.hasFocus())) {
      this.unreadMessages += 1;
    }
  }

  /**
   * Inserts or updates multiple messages.
   *
   * @param {Message} rawMessages - List of rawMessages to add.
   */
  upsertMultipleMessages(rawMessages) {
    if (this.isOnlyHeader && rawMessages?.length) {
      this.messages = [];
    }
    rawMessages?.forEach(rawMessage => this.upsertMessage(rawMessage, true));
    this.unreadMessages = 0;
  }

  /**
   * Find message by row id.
   *
   * @param {String} rowId - The row id to search by.
   */
  findMessageByRowId(rowId) {
    const messageIndex = this.messages.findIndex(message => message.rowId === rowId);

    if (messageIndex !== -1) {
      console.log(`Found message by rowId ${rowId} with index ${messageIndex}`);
      return this.messages[messageIndex];
    }
    return null;
  }

  /**
   * Delete a message by row id.
   *
   * @param {String} rowId - The row id to delete by.
   */
  deleteMessageByRowId(rowId) {
    const messageIndex = this.messages.findIndex(message => message.rowId === rowId);

    if (messageIndex !== -1) {
      console.log(`Found message by rowId ${rowId} with index ${messageIndex}`);
      this.messages.splice(messageIndex, 1);
    }
  }

  /**
   * Resets unread messages counter to zero.
   */
  clearUnreadMessages() {
    this.unreadMessages = 0;
  }

  /**
   * Public static methods
   */

  /**
   * Compares two given Conversation objects for order.
   * Returns a negative integer, zero, or a positive integer as ConversationA object is less than,
   * equal to, or greater than ConversationB object.
   *
   * Descending order.
   *
   * @param {Conversation} conversationA
   * @param {Conversation} conversationB
   *
   * @returns {Number} -1 | 0 | 1
   */
  static compare(conversationA, conversationB) {
    const comparableFieldA = conversationA.newestMessage.originationTime;
    const comparableFieldB = conversationB.newestMessage.originationTime;

    if (comparableFieldA > comparableFieldB) return -1;
    if (comparableFieldA < comparableFieldB) return 1;
    return 0;
  }

  getConversationDisplayName() {
    if (!this.groupMessage) {
      return this.contact.displayName;
    }

    const currentUser = store.getters['user/currentUser'];
    const displayNames = [];
    this.contacts.forEach(contact => {
      if(currentUser.number !== contact.number){
        displayNames.push(contact.displayName);
      }
    });

    return displayNames.join(", ");
  }
}
